import React from 'react';

export interface AuthLayoutProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * AuthLayout - Modern, split-panel layout for authentication pages
 * No sidebar, no header navigation - clean branded experience
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`min-h-screen w-full flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-950 relative overflow-hidden ${className}`}>
      {/* Gradient overlay for entire page */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/30 via-indigo-100/20 to-purple-100/30 dark:from-blue-950/30 dark:via-indigo-950/20 dark:to-purple-950/30"></div>

      {/* Large watermark logo in page background */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] dark:opacity-[0.05]">
        <img
          src="/lithos-logo_logo-dark.png"
          alt=""
          className="w-[800px] h-[800px] object-contain dark:invert"
        />
      </div>

      <div className="w-full max-w-6xl relative z-10">
        <div className="grid md:grid-cols-2 gap-0 bg-white/90 dark:bg-gray-800/90 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">

          {/* Mobile Header - Branded Banner (visible only on mobile) */}
          <div className="md:hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white p-6 sm:p-8 relative overflow-hidden">
            {/* Gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-900/20 to-purple-900/40"></div>

            {/* Decorative background elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/4 -translate-y-1/4"></div>
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 translate-y-1/4"></div>
            </div>

            {/* Mobile branding content */}
            <div className="relative z-10 text-center">
              <img
                src="/lithos-logo_logo-long-dark.png"
                alt="Lithos Logo"
                className="h-10 sm:h-12 w-auto brightness-0 invert mx-auto mb-3"
              />
              <h1 className="text-xl sm:text-2xl font-bold leading-tight">
                Welcome to Lithos
              </h1>
              <p className="text-blue-100 text-sm sm:text-base mt-2">
                Construction project management simplified
              </p>
            </div>
          </div>

          {/* Left Panel - Branding & Hero (desktop only) */}
          <div className="hidden md:flex flex-col justify-between p-12 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
            {/* Gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-900/20 to-purple-900/40"></div>

            {/* Decorative background elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
            </div>

            {/* Logo and branding */}
            <div className="relative z-10">
              <div className="mb-8">
                <img
                  src="/lithos-logo_logo-long-dark.png"
                  alt="Lithos Logo"
                  className="h-16 w-auto brightness-0 invert"
                />
              </div>
              <h1 className="text-4xl font-bold mb-4 leading-tight">
                Welcome to Lithos
              </h1>
              <p className="text-blue-100 text-lg">
                Streamline your construction projects with powerful tools for job tracking, inventory management, and team collaboration.
              </p>
            </div>

            {/* Features list */}
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-blue-50">Real-time project tracking</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-blue-50">Inventory & material management</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-blue-50">Team collaboration tools</span>
              </div>
            </div>
          </div>

          {/* Right Panel - Auth Form */}
          <div className="flex flex-col justify-center p-6 sm:p-8 md:p-12 bg-white/95 dark:bg-gray-800/95 min-h-[500px] sm:min-h-[600px]">
            {/* Form content */}
            <div className="w-full max-w-md mx-auto">
              {children}
            </div>

            {/* Footer */}
            <div className="text-center mt-6 sm:mt-8 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              &copy; {new Date().getFullYear()} Lithos. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
